// =============================================================================
// OELOX — IndexedDB layer (Dexie) for POS PWA offline storage
// =============================================================================
// PRIMARY SOURCE OF TRUTH for all offline data.
// React Query syncs network → IndexedDB → UI (never the reverse).
// =============================================================================

import Dexie, { type Table } from "dexie";

// ── TTL constants ───────────────────────────────────────────────────────────

const MENU_CACHE_TTL = 24 * 60 * 60 * 1000;   // 24 hours
const ORDERS_CACHE_TTL = 30 * 60 * 1000;       // 30 minutes

// ── Interfaces ──────────────────────────────────────────────────────────────

export interface QueuedAction {
  id: string;
  type: string;
  payload: unknown;
  timestamp: number;
  retryCount: number;
}

export interface CartLine {
  menu_item_id: string;
  item_name: string;
  quantity: number;
  unit_price: number;
  image_url?: string | null;
}

export interface CachedMenuItem {
  id: string;
  tenant_id: string;
  name: string;
  base_price: number;
  category_id: string | null;
  image_url: string | null;
  cached_at: number;
}

export interface CachedTenant {
  id: string;
  name: string;
  currency: string;
  profile_tenant_id: string;
  user_id: string;
  cached_at: number;
}

export interface CachedCategory {
  id: string;
  tenant_id: string;
  name: string;
  sort_order: number;
  cached_at: number;
}

export interface CachedOrder {
  id: string;
  tenant_id: string;
  displayNumber: string;
  customer_name: string;
  status: string;
  deskStep: string;
  total: number;
  source: string;
  fulfillment_type: string;
  created_at: string;
  cached_at: number;
}

export interface CachedDriver {
  id: string;
  tenant_id: string;
  name: string;
  phone: string | null;
  vehicle_type: string | null;
  is_online: boolean;
  is_verified: boolean;
  collects_cod: boolean;
  link_status: string;
  cached_at: number;
}

// ── Database ────────────────────────────────────────────────────────────────

export class OeloxPosDB extends Dexie {
  queue!: Table<QueuedAction, string>;
  dead_letter!: Table<QueuedAction, string>;
  cart!: Table<CartLine, string>;
  menu_items!: Table<CachedMenuItem, string>;
  menu_categories!: Table<CachedCategory, string>;
  cached_orders!: Table<CachedOrder, string>;
  cached_drivers!: Table<CachedDriver, string>;
  tenant_cache!: Table<CachedTenant, string>;

  constructor() {
    super("oelox-pos-db");
    this.version(6).stores({
      queue: "id, type, timestamp, retryCount",
      dead_letter: "id, type, timestamp, retryCount",
      cart: "menu_item_id",
      menu_items: "id, tenant_id, category_id",
      menu_categories: "id, tenant_id",
      cached_orders: "id, tenant_id, deskStep, created_at",
      cached_drivers: "id, tenant_id, link_status",
      tenant_cache: "user_id",
    });
  }
}

export const db = new OeloxPosDB();

// ── In-memory cache for synchronous component access ───────────────────────
// IndexedDB is async, but React components need data synchronously on mount.
// This module-level cache mirrors IndexedDB so components can render instantly
// from memory without waiting for async IndexedDB reads.

const menuItemsMemCache = new Map<string, CachedMenuItem[]>();
const categoriesMemCache = new Map<string, CachedCategory[]>();
let cartMemCache: CartLine[] = [];
const ordersMemCache = new Map<string, CachedOrder[]>();  // key: tenantId:deskStep

/** Synchronous read — returns [] if not yet cached. */
export function getCachedMenuItemsSync(tenantId: string): CachedMenuItem[] {
  return menuItemsMemCache.get(tenantId) ?? [];
}

/** Synchronous read — returns [] if not yet cached. */
export function getCachedCategoriesSync(tenantId: string): CachedCategory[] {
  return categoriesMemCache.get(tenantId) ?? [];
}

/** Synchronous read — instant cart access, no flash on mount. */
export function getCartSync(): CartLine[] {
  return cartMemCache;
}

/** Synchronous read — returns [] if not yet cached. Key: `${tenantId}:${deskStep}`. */
export function getCachedOrdersSync(tenantId: string, deskStep?: string): CachedOrder[] {
  const key = deskStep ? `${tenantId}:${deskStep}` : `${tenantId}:__all__`;
  return ordersMemCache.get(key) ?? [];
}

/** One-time preload from IndexedDB → memory on module init (page load). */
let primeDone = false;
export async function primeMenuCacheFromIndexedDB(): Promise<void> {
  if (primeDone) return;
  try {
    const allItems = await db.menu_items.toArray();
    // Rebuild from scratch — never append (prevents duplicates on re-call)
    const itemsByTenant = new Map<string, CachedMenuItem[]>();
    for (const item of allItems) {
      const arr = itemsByTenant.get(item.tenant_id);
      if (arr) arr.push(item);
      else itemsByTenant.set(item.tenant_id, [item]);
    }
    menuItemsMemCache.clear();
    for (const [tid, items] of itemsByTenant) {
      menuItemsMemCache.set(tid, items);
    }

    const allCats = await db.menu_categories.orderBy("sort_order").toArray();
    const catsByTenant = new Map<string, CachedCategory[]>();
    for (const cat of allCats) {
      const arr = catsByTenant.get(cat.tenant_id);
      if (arr) arr.push(cat);
      else catsByTenant.set(cat.tenant_id, [cat]);
    }
    categoriesMemCache.clear();
    for (const [tid, cats] of catsByTenant) {
      categoriesMemCache.set(tid, cats);
    }

    primeDone = true;
  } catch { /* IndexedDB unavailable — silent */ }
}

// Cart is preloaded in main.tsx via loadCart() alongside primeMenuCacheFromIndexedDB().
// Both must complete before React renders to prevent UI flash.

// Preload is now called explicitly from main.tsx (awaited before first render).
// The fire-and-forget duplicate has been removed to prevent double-priming
// which caused menu items to appear twice in the catalog.

// ── Cart helpers ────────────────────────────────────────────────────────────

export async function loadCart(): Promise<CartLine[]> {
  try {
    const lines = await db.cart.toArray();
    cartMemCache = lines;
    return lines;
  } catch { return []; }
}

export async function saveCart(lines: CartLine[]): Promise<void> {
  cartMemCache = lines;
  try {
    await db.cart.clear();
    if (lines.length > 0) await db.cart.bulkPut(lines);
  } catch { /* silent */ }
}

export async function clearCart(): Promise<void> {
  cartMemCache = [];
  try { await db.cart.clear(); } catch { /* silent */ }
}

// ── Menu cache helpers ──────────────────────────────────────────────────────

export async function getCachedMenuItems(tenantId: string): Promise<CachedMenuItem[]> {
  try {
    const items = await db.menu_items.where("tenant_id").equals(tenantId).toArray();
    const cutoff = Date.now() - MENU_CACHE_TTL;
    return items.filter((i) => i.cached_at > cutoff);
  } catch {
    return [];
  }
}

export async function cacheMenuItems(tenantId: string, items: CachedMenuItem[]): Promise<void> {
  // Write to in-memory cache synchronously first (instant component access)
  menuItemsMemCache.set(tenantId, items);
  try {
    await db.menu_items.where("tenant_id").equals(tenantId).delete();
    const now = Date.now();
    await db.menu_items.bulkPut(items.map((i) => ({ ...i, cached_at: now })));
  } catch { /* silent */ }
}

// ── Tenant cache helpers ────────────────────────────────────────────────────

export async function cacheTenant(data: CachedTenant): Promise<void> {
  try {
    await db.tenant_cache.put({ ...data, cached_at: Date.now() });
  } catch { /* silent */ }
}

export async function getCachedTenant(userId: string): Promise<CachedTenant | null> {
  try {
    const t = await db.tenant_cache.get(userId);
    if (!t) return null;
    // 7-day TTL for tenant cache — but NEVER delete when offline.
    // Expired cache is better than no cache (offline-first principle).
    if (Date.now() - t.cached_at > 7 * 24 * 60 * 60 * 1000) {
      if (typeof navigator !== "undefined" && navigator.onLine) {
        await db.tenant_cache.delete(userId);
        return null; // online: force refresh from network
      }
      // offline: return expired cache — it's all we have
    }
    return t;
  } catch {
    return null;
  }
}

// ── Category cache helpers ──────────────────────────────────────────────────

export async function getCachedCategories(tenantId: string): Promise<CachedCategory[]> {
  try {
    return await db.menu_categories.where("tenant_id").equals(tenantId).sortBy("sort_order");
  } catch {
    return [];
  }
}

export async function cacheCategories(tenantId: string, cats: CachedCategory[]): Promise<void> {
  // Write to in-memory cache synchronously first (instant component access)
  categoriesMemCache.set(tenantId, cats);
  try {
    await db.menu_categories.where("tenant_id").equals(tenantId).delete();
    if (cats.length > 0) await db.menu_categories.bulkPut(cats);
  } catch { /* silent */ }
}

// ── Orders cache helpers ────────────────────────────────────────────────────

export async function getCachedOrders(tenantId: string, deskStep?: string): Promise<CachedOrder[]> {
  try {
    let collection = db.cached_orders.where("tenant_id").equals(tenantId);
    if (deskStep) collection = collection.and((o) => o.deskStep === deskStep);
    const orders = await collection.sortBy("created_at");
    const cutoff = Date.now() - ORDERS_CACHE_TTL;
    return orders.filter((o) => o.cached_at > cutoff);
  } catch {
    return [];
  }
}

export async function cacheOrders(tenantId: string, orders: CachedOrder[]): Promise<void> {
  try {
    // Only keep last 100 orders per tenant
    const existing = await db.cached_orders.where("tenant_id").equals(tenantId).toArray();
    const keep = new Map(existing.map((o) => [o.id, o]));
    for (const o of orders) keep.set(o.id, o);
    const all = Array.from(keep.values()).sort((a, b) => b.created_at.localeCompare(a.created_at));
    const trimmed = all.slice(0, 100);
    await db.cached_orders.where("tenant_id").equals(tenantId).delete();
    if (trimmed.length > 0) await db.cached_orders.bulkPut(trimmed);

    // Update sync memory cache per deskStep
    for (const step of ["pending", "kitchen", "ready", "driver", "done"]) {
      const key = `${tenantId}:${step}`;
      ordersMemCache.set(key, trimmed.filter((o) => o.deskStep === step));
    }
    ordersMemCache.set(`${tenantId}:__all__`, trimmed);
  } catch { /* silent */ }
}

// ── Driver cache helpers ────────────────────────────────────────────────────

export async function getCachedDrivers(tenantId: string): Promise<CachedDriver[]> {
  try {
    return await db.cached_drivers.where("tenant_id").equals(tenantId).toArray();
  } catch {
    return [];
  }
}

export async function cacheDrivers(tenantId: string, drivers: CachedDriver[]): Promise<void> {
  try {
    await db.cached_drivers.where("tenant_id").equals(tenantId).delete();
    if (drivers.length > 0) await db.cached_drivers.bulkPut(drivers);
  } catch { /* silent */ }
}

// ── One-time migration from localStorage ────────────────────────────────────

const MIGRATED_KEY = "oelox_idxb_migrated";

export async function migrateFromLocalStorage(): Promise<void> {
  if (typeof window === "undefined") return;
  try {
    const currentVersion = localStorage.getItem(MIGRATED_KEY);

    // v1 migration: queue + dead-letter
    if (!currentVersion) {
      const QUEUE_KEY = "oelox_offline_queue";
      const DEAD_KEY = "oelox_dead_letter_queue";

      const rawQueue = localStorage.getItem(QUEUE_KEY);
      if (rawQueue) {
        try {
          const items: QueuedAction[] = JSON.parse(rawQueue);
          if (items.length > 0) await db.queue.bulkPut(items);
        } catch { /* corrupt */ }
        localStorage.removeItem(QUEUE_KEY);
      }

      const rawDead = localStorage.getItem(DEAD_KEY);
      if (rawDead) {
        try {
          const items: QueuedAction[] = JSON.parse(rawDead);
          if (items.length > 0) await db.dead_letter.bulkPut(items);
        } catch { /* corrupt */ }
        localStorage.removeItem(DEAD_KEY);
      }
    }

    // v2 migration: cart
    if (!currentVersion || currentVersion === "v1") {
      const rawCart = localStorage.getItem("oelox-pos-cart");
      if (rawCart) {
        try {
          const items: CartLine[] = JSON.parse(rawCart);
          if (items.length > 0) {
            await db.cart.clear();
            await db.cart.bulkPut(items);
          }
        } catch { /* corrupt */ }
        localStorage.removeItem("oelox-pos-cart");
      }
    }

    localStorage.setItem(MIGRATED_KEY, "v2");
  } catch { /* IndexedDB unavailable */ }
}
